"use client";

/**
 * Linked accounts as rows with an inline player where the platform offers a
 * key-free public embed (Spotify, Apple Music albums/songs/playlists, Deezer,
 * SoundCloud, Mixcloud, YouTube / YouTube Music videos, Audius). Everything
 * else is a row with an Open link. Platforms + embeds come from the shared
 * registry in lib/social-platforms.ts.
 */

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getEmbed, normalizeSocialLinks, type LinkedAccount } from "@/lib/social-platforms";
import { PlatformLogo } from "@/components/social/platform-logo";
import { useAccountLabel } from "@/components/social/linked-accounts";

/** Which embeddable link opens first. */
const EMBED_PRIORITY = ["soundcloud", "mixcloud", "spotify", "applemusic", "deezer", "youtubemusic", "audius", "youtube"];

/** Same attributes as the original Spotify embed — shared by every player. */
export function PlatformEmbedFrame({ src, height, title }: { src: string; height: number; title: string }) {
  return (
    <iframe
      key={src}
      src={src}
      title={title}
      width="100%"
      height={height}
      frameBorder="0"
      scrolling="no"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      className="block w-full rounded-lg"
    />
  );
}

interface SocialEmbedsProps {
  /** Any stored shape (map or Social Hub array) — normalised here. */
  socialLinks?: unknown;
  /** Already-normalised links (wins over socialLinks). */
  links?: LinkedAccount[];
  name: string;
  /** Only list links that can play here (when the plain links are shown elsewhere). */
  playableOnly?: boolean;
}

export function SocialEmbeds({ socialLinks, links, name, playableOnly = false }: SocialEmbedsProps) {
  const t = useTranslations("profile.socialHub.embeds");
  const labelOf = useAccountLabel();
  const rows = useMemo(() => {
    const all = links ?? normalizeSocialLinks(socialLinks);
    return all
      .map((account) => ({ account, embed: getEmbed(account), key: `${account.id}-${account.url}` }))
      .filter((r) => !playableOnly || r.embed);
  }, [links, socialLinks, playableOnly]);

  // undefined = "not touched yet" → auto-open the first playable link, even
  // when links arrive after the first render (artist page merges a linked
  // user's accounts in later).
  const [expanded, setExpanded] = useState<string | null | undefined>(undefined);
  const defaultKey = useMemo(() => {
    for (const id of EMBED_PRIORITY) {
      const hit = rows.find((r) => r.account.id === id && r.embed);
      if (hit) return hit.key;
    }
    return null;
  }, [rows]);
  const openKey = expanded === undefined ? defaultKey : expanded;

  if (rows.length === 0) return null;

  return (
    <div className="space-y-3 min-w-0">
      {rows.map(({ account, embed, key }) => {
        const platform = labelOf(account);
        const isOpen = openKey === key && !!embed;
        return (
          <div key={key} className="rounded-xl border border-border/60 overflow-hidden">
            <div className="flex items-center gap-3 px-3 py-2 sm:px-4 bg-secondary/30">
              <PlatformLogo id={account.id} url={account.url} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{platform}</p>
                <p className="text-xs text-muted-foreground truncate">{account.url.replace(/^https?:\/\/(www\.)?/, "")}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {embed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-11 sm:h-9 gap-1 text-xs"
                    onClick={() => setExpanded(isOpen ? null : key)}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? t("hideAria", { platform }) : t("playAria", { platform })}
                  >
                    {isOpen ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
                    {isOpen ? t("hide") : t("play")}
                  </Button>
                )}
                <Button asChild variant="outlineContrast" size="sm" className="h-11 sm:h-9 gap-1.5 text-xs">
                  <a href={account.url} target="_blank" rel="noopener noreferrer" aria-label={t("openAria", { platform })}>
                    <ExternalLink aria-hidden="true" /> {t("open")}
                  </a>
                </Button>
              </div>
            </div>
            {isOpen && embed && (
              <div className="p-2 sm:p-3 bg-background/40">
                <PlatformEmbedFrame src={embed.src} height={embed.height} title={t("playerTitle", { platform, name })} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
